import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import type { SurveyVideoProps } from "../../lib/schema";
import type { StatData } from "../../types/stats";
import { TEXT_PRIMARY, TEXT_SECONDARY } from "../../lib/colors";

type IntroProps = Pick<SurveyVideoProps, "itemX" | "itemY" | "cities"> & {
  data: StatData;
};

const SPRING_CONFIG = { damping: 14, stiffness: 120, mass: 0.8 } as const;

/** イントロシーン (0-150f / 5s): タイトル、品目・都市名を spring で登場 */
const Intro: React.FC<IntroProps> = ({ itemX, itemY, cities }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const displayCities =
    cities.length === 0 ? "全都市" : cities.join("・");

  // ── 登場アニメーション (時差 spring) ──────────────
  const enterTitle = spring({ frame, fps, config: SPRING_CONFIG, durationInFrames: 60 });
  const enterItems = spring({ frame: frame - 10, fps, config: SPRING_CONFIG, durationInFrames: 60 });
  const enterCities = spring({ frame: frame - 20, fps, config: SPRING_CONFIG, durationInFrames: 60 });
  const enterYear = spring({ frame: frame - 30, fps, config: SPRING_CONFIG, durationInFrames: 60 });

  // ── フェードアウト (最後 30f) ─────────────────────
  const fadeOut = interpolate(
    frame,
    [durationInFrames - 30, durationInFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Meiryo, 'Noto Sans JP', sans-serif",
        gap: 32,
        opacity: fadeOut,
      }}
    >
      {/* タイトル */}
      <div
        style={{
          fontSize: 77,
          fontWeight: 700,
          color: TEXT_PRIMARY,
          textShadow: "0 0 40px rgba(100,180,255,0.4)",
          transform: `translateY(${(1 - enterTitle) * 40}px)`,
          opacity: enterTitle,
        }}
      >
        家計調査 支出データの推移
      </div>

      {/* 品目 X × Y */}
      <div
        style={{
          fontSize: 48,
          color: TEXT_SECONDARY,
          transform: `translateY(${(1 - enterItems) * 30}px)`,
          opacity: enterItems,
        }}
      >
        {itemX} × {itemY}
      </div>

      {/* 都市名 */}
      <div
        style={{
          fontSize: 34,
          color: "#7a94b8",
          marginTop: 16,
          transform: `translateY(${(1 - enterCities) * 25}px)`,
          opacity: enterCities,
        }}
      >
        {displayCities}
      </div>

      {/* 年範囲 */}
      <div
        style={{
          fontSize: 29,
          color: "#5a7498",
          marginTop: 8,
          transform: `translateY(${(1 - enterYear) * 20}px)`,
          opacity: enterYear,
        }}
      >
        2000 → 2025
      </div>
    </AbsoluteFill>
  );
};

export default Intro;
